import { IonAvatar, IonButton, IonButtons, IonChip, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonProgressBar, IonText, IonTitle, IonToolbar, useIonAlert, IonToggle, useIonModal } from '@ionic/react';
import React, { memo, useEffect, useState } from "react";
import OCPlatform from '../client/OCPlatform';
import OCPushNotifications from "../client/OCPushNotifications";
import OCAuth from '../client/OCAuth';
import { CapacitorCrisp } from '@capgo/capacitor-crisp';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import SoftwareUpdateIndicatorButton from '../components/SoftwareUpdateIndicatorButton';
import { keyOutline, logoFacebook, logoGithub, logoInstagram, logoLinkedin, logOutOutline, logoTiktok, logoYoutube, shieldHalfOutline } from 'ionicons/icons';
import styles from './Account.module.css';
import { useCurrentUserQuery, useLogoutMutation } from '../query/currentUserQuery';
import { useGatewayConnectionStateQuery } from '../query/gatewayConnectionStateQuery';
import { useDeveloperModeQuery, useUpdateDeveloperModeMutation } from '../query/developerModeQuery';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SectionHeader from '../components/SectionHeader';
import sectionStyles from '../components/SectionHeader.module.css';
import { useQuery } from '@tanstack/react-query';
import packageJson from '../../package.json';
import PushNotificationSettingsModal from '../modals/PushNotificationSettingsModal';
import { usePushNotificationConfigQuery } from '../query/pushNotificationConfigQuery';
import ApiKeysModal from '../modals/ApiKeysModal';

const developerRepositoryUrl = 'https://github.com/OnlyCatAI/onlycat-developer';

const Account: React.FC = () => {
  const { t } = useTranslation('pages');
  const { t: tCommon } = useTranslation('common');
  const currentUserQuery = useCurrentUserQuery();
  const user = currentUserQuery.data;
  const [deviceToken, setDeviceToken] = useState<string | undefined>();
  const [pushEnvironment, setPushEnvironment] = useState<string | undefined>();
  const developerModeQuery = useDeveloperModeQuery();
  const updateDeveloperModeMutation = useUpdateDeveloperModeMutation();
  const logoutMutation = useLogoutMutation();
  const pushNotificationConfigQuery = usePushNotificationConfigQuery();
  const developerMode = !!developerModeQuery.data;
  const [presentAlert] = useIonAlert();
  const gatewayConnectionStateQuery = useGatewayConnectionStateQuery();
  const history = useHistory();
  const location = useLocation();
  const appVersionQuery = useQuery({
    queryKey: ['appVersion', 'bundle'],
    queryFn: async () => {
      try {
        const current = await CapacitorUpdater.current();
        return current.bundle?.version || packageJson.version || '-';
      } catch (error) {
        console.warn('Failed to load bundle version', error);
        return packageJson.version || '-';
      }
    },
    staleTime: Infinity,
  });
  const appVersion = appVersionQuery.data ?? packageJson.version ?? '-';
  const pushNotificationStatus = pushNotificationConfigQuery.data?.pushEnabled
    ? t('account.push_notifications_on')
    : t('account.push_notifications_off');

  const [presentPushNotificationSettingsModal, dismissPushNotificationSettingsModal] = useIonModal(PushNotificationSettingsModal, {
    dismiss: (data?: string, role?: string) => dismissPushNotificationSettingsModal(data, role)
  });
  const [presentApiKeysModal, dismissApiKeysModal] = useIonModal(ApiKeysModal, {
    dismiss: (data?: string, role?: string) => dismissApiKeysModal(data, role)
  });

  const openPushNotificationSettings = () => {
    presentPushNotificationSettingsModal({
      presentingElement: document.getElementsByTagName('ion-router-outlet')[0],
      cssClass: 'oc-dialog-m'
    });
  };

  const openApiKeys = () => {
    presentApiKeysModal({
      presentingElement: document.getElementsByTagName('ion-router-outlet')[0],
      cssClass: 'oc-dialog-m'
    });
  };

  const login = async () => {
    await OCAuth.login();
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  }

  useEffect(() => {
    OCPlatform.getDeviceToken().then(setDeviceToken);
    OCPushNotifications.getPushEnvironment().then(setPushEnvironment);
  }, []);

  // Update Crisp user information when user logs in or out
  useEffect(() => {
    if (user) {
      // Set user information in Crisp when logged in
      CapacitorCrisp.setUser({
        email: user.email || undefined,
        nickname: user.name || undefined
      });

      CapacitorCrisp.setTokenID({ tokenID: String(user.sub) });
    } else {
      // Reset user data when logged out
      CapacitorCrisp.reset();
    }
  }, [user]);

  useEffect(() => {
    async function handleAuthError(event: any) {
      const err = event.detail;

      await presentAlert({
        header: t('account.login_error'),
        subHeader: err.message,
        message: t('account.login_error_message'),
        buttons: [tCommon('buttons.dismiss')],
      });
    }

    document.addEventListener('oc:auth:error', handleAuthError);

    return () => {
      document.removeEventListener('oc:auth:error', handleAuthError);
    }
  }, [presentAlert]);

  // Check for redirect path in query string and redirect if user is authenticated
  useEffect(() => {
    // Parse the query string to get the path parameter
    const queryParams = new URLSearchParams(location.search);
    const redirectPath = queryParams.get('path');

    // Only redirect if user is authenticated and there's a redirect path
    if (user && redirectPath) {
      // Clear the query parameter by replacing the current URL without the query
      history.replace({
        pathname: location.pathname
      });

      // Redirect to the intended destination
      history.push(redirectPath);
    }
  }, [user, location, history]);

  const toggleDeveloperMode = async (e: CustomEvent) => {
    const isEnabled = e.detail.checked;
    await updateDeveloperModeMutation.mutateAsync(isEnabled);
  };

  const openCrispChat = async () => {
    await CapacitorCrisp.openMessenger();
  };

  const openExternalUrl = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('account.title')}</IonTitle>
          <IonButtons slot="primary">
            <SoftwareUpdateIndicatorButton />
          </IonButtons>
          {!!(gatewayConnectionStateQuery.isConnecting || currentUserQuery.isLoading || logoutMutation.isPending || pushNotificationConfigQuery.isFetching) && <IonProgressBar type="indeterminate" />}
        </IonToolbar>
      </IonHeader>
      <IonContent className={styles.accountContent} color="light" fullscreen>
        <div className="oc-page-container">
        {<div className={styles.avatarContainer}>
          <IonAvatar>
            <img src={user?.avatarUrl ?? '/assets/account/default-avatar.svg'} alt={user?.name ?? 'Guest'} referrerPolicy="no-referrer" />
          </IonAvatar>
          <h1>{user?.name ?? t('account.not_signed_in')}</h1>
          <h2>{user?.email}</h2>
        </div>}

        {!user && <IonButton onClick={login} fill="outline" shape="round" color="primary" expand="full">
          <IonIcon slot="start" icon={logOutOutline}></IonIcon>
          {t('account.sign_in')}
        </IonButton>}

        <SectionHeader>{t('account.user_information')}</SectionHeader>
        <IonList inset className={sectionStyles.insetList}>
          <IonItem>
            <IonLabel>{t('account.user_id')}</IonLabel>
            {user?.id ?? '-'}
          </IonItem>
          {user?.userLevel && <IonItem>
            <IonLabel>{t('account.user_level')}</IonLabel>
            <IonChip color="primary">
              <IonIcon icon={shieldHalfOutline}></IonIcon>
              <IonLabel>{user.userLevel}</IonLabel>
            </IonChip>
          </IonItem>}
          {user && <IonItem button routerLink="/account/delete">
            <IonLabel color="danger">{t('account.delete_account')}</IonLabel>
          </IonItem>}
        </IonList>

        <SectionHeader>{t('account.notifications')}</SectionHeader>
        <IonList inset className={sectionStyles.insetList}>
          <IonItem button detail onClick={openPushNotificationSettings}>
            <IonLabel>{t('account.push_notifications')}</IonLabel>
            {pushNotificationConfigQuery.data && (
              <IonText slot="end">{pushNotificationStatus}</IonText>
            )}
          </IonItem>
          <IonItem>
            <IonLabel>{t('account.push_environment')}</IonLabel>
            {pushEnvironment}
          </IonItem>
        </IonList>

        <SectionHeader>{t('account.developer_settings')}</SectionHeader>
        <IonList inset className={sectionStyles.insetList}>
          <IonItem>
            <IonToggle checked={developerMode} onIonChange={toggleDeveloperMode} helperText={t('account.developer_mode_help')}>
              {t('account.developer_mode')}
            </IonToggle>
          </IonItem>
          {developerMode && <IonItem button detail onClick={() => openExternalUrl(developerRepositoryUrl)}>
            <IonIcon slot="start" icon={logoGithub} />
            <IonLabel>{t('account.developer_resources')}</IonLabel>
          </IonItem>}
          {developerMode && user && <IonItem button detail onClick={openApiKeys}>
            <IonIcon slot="start" icon={keyOutline} />
            <IonLabel>{t('account.api_keys')}</IonLabel>
          </IonItem>}
          {developerMode && <IonItem>
            <IonLabel>{t('account.device_token')}</IonLabel>
            {deviceToken}
          </IonItem>}
        </IonList>

        <LanguageSwitcher />

        <SectionHeader>{t('account.support')}</SectionHeader>
        <IonList inset className={sectionStyles.insetList}>
          <IonItem>
            <IonLabel>{t('account.app_version')}</IonLabel>
            {appVersion}
          </IonItem>
          <IonItem button onClick={openCrispChat}>
            <IonLabel color="primary">{t('account.contact_support')}</IonLabel>
          </IonItem>
        </IonList>
        <SectionHeader>{t('account.social_media')}</SectionHeader>
        <IonList inset className={sectionStyles.insetList}>
          <IonItem>
            <IonIcon onClick={() => openExternalUrl('https://www.facebook.com/OnlyCatAI')} icon={logoFacebook} className={styles.socialIcons}/>
            <IonIcon onClick={() => openExternalUrl('https://www.instagram.com/onlycat_ai/')} icon={logoInstagram} className={styles.socialIcons}/>
            <IonIcon onClick={() => openExternalUrl('https://www.linkedin.com/company/onlycat/posts/?feedView=all')} icon={logoLinkedin} className={styles.socialIcons}/>
            <IonIcon onClick={() => openExternalUrl('https://www.tiktok.com/@onlycatai')} icon={logoTiktok} className={styles.socialIcons}/>
            <IonIcon onClick={() => openExternalUrl('https://x.com/OnlyCatAI')} src="/assets/social/x-logo.svg"  className={styles.socialIcons}/>
            <IonIcon onClick={() => openExternalUrl('https://www.youtube.com/channel/UCJ8Apoxdt9YfCR20J8-peTw')} icon={logoYoutube} className={styles.socialIcons}/>
          </IonItem>
        </IonList>
        {user && <IonButton onClick={logout} fill="clear" shape="round" color="danger" expand="full">
          <IonIcon slot="start" icon={logOutOutline}></IonIcon>
          {t('account.sign_out')}
        </IonButton>}
        </div>
      </IonContent>
    </IonPage >
  );
};

export default memo(Account);
